import { useCallback, useEffect, useState } from 'react';

import { requestBrowserLocation, reverseGeocode } from './geo.js';

const STORAGE_KEY = 'hackfind.location';

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Number.isFinite(parsed?.lat) && Number.isFinite(parsed?.long) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Resolves where the user is.
 *
 * On load we ask the browser for geolocation. If they decline (or it fails), status
 * becomes 'denied' and the UI offers the manual city-search fallback instead. A chosen
 * location is remembered in localStorage so we don't re-prompt on every visit.
 */
export function useUserLocation() {
  const [location, setLocation] = useState(readStored);
  const [status, setStatus] = useState(() => (readStored() ? 'ready' : 'idle'));
  const [error, setError] = useState(null);

  const persist = useCallback((next) => {
    setLocation(next);
    setStatus(next ? 'ready' : 'idle');
    try {
      if (next) localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      // localStorage can be unavailable (private mode, blocked cookies) — not fatal.
    }
  }, []);

  const requestBrowser = useCallback(async () => {
    setStatus('locating');
    setError(null);
    try {
      const position = await requestBrowserLocation();
      // Coordinates alone can't tell us the user's state, which the "Statewide" radius
      // preset needs — so we reverse-geocode for the label. Failure is non-fatal.
      let place = {};
      try {
        place = await reverseGeocode(position);
      } catch {
        place = {};
      }
      persist({ ...position, ...place });
    } catch (err) {
      setStatus('denied');
      setError(err.message);
    }
  }, [persist]);

  // Ask once on first load, unless we already have a remembered location.
  useEffect(() => {
    if (status === 'idle' && !location) requestBrowser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setManualLocation = useCallback(
    (choice) => {
      persist({ ...choice, source: 'search' });
      setError(null);
    },
    [persist],
  );

  const clearLocation = useCallback(() => persist(null), [persist]);

  return { location, status, error, requestBrowser, setManualLocation, clearLocation };
}
